import type { Dictionary } from "@kensio/pinyinjs";

import type { CanonicalEntry } from "../segmenting/canonical-entry.js";
import { CidianIndex } from "../segmenting/cidian-index.js";
import type { CopyFlavours } from "./clipboard.js";
import type { ServedTier } from "../segmenting/dictionary.js";
import { shownWordsCopy } from "./segmenter-copy.js";
import { SegmenterDictionary } from "./segmenter-dictionary.js";
import type { SegmenterElements } from "./segmenter-elements.js";
import { SegmenterGlosses } from "./segmenter-glosses.js";
import { holdToCap } from "./segmenter-input.js";
import { SegmenterLookups } from "./segmenter-lookups.js";
import { SegmenterSaving } from "./segmenter-saving.js";
import { SegmenterView } from "./segmenter-view.js";
import {
  type SegmentedWord,
  segmentedWords,
} from "../segmenting/segmented-words.js";
import type { WordOrder } from "../segmenting/word-order.js";
import { currentState, startingState, type Startup } from "./tool-state.js";
import { rememberUrl, type WritableState } from "./url-state.js";
import { SegmenterSavedWords } from "./segmenter-saved-words.js";

/**
 * What the segmenter knows: a dictionary, a text and an order.
 *
 * Apart from `segmenter-tool.ts`, which is the page half — the buttons, the
 * listeners and the one exported function the bundle calls. Everything here
 * can be asked for an answer without a click having happened, and nothing here
 * knows which control asked.
 */

export class SegmenterSession {
  readonly #elements: SegmenterElements;
  readonly #startup: Startup;
  readonly #savedWords: SegmenterSavedWords;
  readonly #dictionaryLoader: SegmenterDictionary;
  readonly #glosses: SegmenterGlosses;

  #dictionary: Dictionary | undefined;
  #cidian = new CidianIndex();
  #words: readonly SegmentedWord[] = [];
  #order: WordOrder = "count";

  constructor(elements: SegmenterElements, dictionaryUrl: string) {
    this.#elements = elements;
    const lookups = new SegmenterLookups();

    /*
     * The view is built first and reads the meanings through a closure, which
     * is what lets each of these be told about the other. It also keeps the
     * view ignorant of where a meaning came from.
     */
    const view = new SegmenterView(elements, {
      entryOf: (headword): CanonicalEntry | undefined =>
        this.#glosses.known(headword),
      onOpened: (headword): void => {
        lookups.opened(headword);
      },
    });

    this.#glosses = new SegmenterGlosses({
      onEntry: (headword, entry): void => {
        view.showGloss(headword, entry);
      },
    });
    this.#savedWords = new SegmenterSavedWords(
      view,
      new SegmenterSaving(elements),
      this.#draw.bind(this),
    );
    this.#startup = startingState(elements, dictionaryUrl, (): void =>
      this.#draw(),
    );
    this.#dictionaryLoader = new SegmenterDictionary(
      this.#startup,
      (dictionary): void => {
        this.#dictionary = dictionary;
        this.segment();
      },
    );

    // A link can carry more text than the box takes, and the counter says so.
    holdToCap(elements);
  }

  /** What the address bar carries, and what Copy link copies. */
  state(): WritableState {
    const { settings, tiers } = this.#startup;

    return currentState(this.#elements, settings, tiers);
  }

  /** Segment what is in the box, then draw it. */
  segment(): void {
    const dictionary = this.#dictionary;
    if (dictionary === undefined) {
      return;
    }

    holdToCap(this.#elements);

    const text = this.#elements.input.value;
    this.#words = segmentedWords(dictionary, text, this.#cidian);

    void this.#savedWords.read(this.#words);
    this.#draw();
    this.#glosses.read(this.#words);
    rememberUrl(this.state());
  }

  /**
   * Put the current list on the page, in the current order.
   *
   * Apart from {@link SegmenterSession.segment} because reordering and
   * changing the pinyin notation both land here and neither needs the text
   * read again. Ordering a few hundred gathered words is the cheap half.
   */
  #draw(): void {
    const { notation } = this.#startup.settings.options;

    this.#savedWords.show(this.#words, this.#order, notation);
  }

  /** Show the same words again, in the order a reader asked for. */
  reorder(order: WordOrder): void {
    this.#order = order;
    this.#draw();
  }

  /**
   * What the copy button puts on the clipboard.
   *
   * The list in the order it is shown rather than the order it was gathered
   * in, so that a spreadsheet somebody pastes into arrives sorted the way the
   * page was.
   */
  copied(): CopyFlavours | undefined {
    const { notation } = this.#startup.settings.options;

    return shownWordsCopy(this.#words, this.#order, notation);
  }

  /**
   * Take the address index, and read the text again with it.
   *
   * Whenever it arrives rather than before the dictionary does. The two are
   * independent, and the list is worth drawing without the index. Whichever of
   * them is second is what draws the links.
   */
  useCidian(index: CidianIndex): void {
    this.#cidian = index;
    this.segment();
  }

  /** Take a dictionary and read the text with it. */
  async load(tier: ServedTier): Promise<void> {
    await this.#dictionaryLoader.load(tier);
  }

  /** Offer the other dictionary tiers, and load the one this visit wants. */
  async open(): Promise<void> {
    await this.#dictionaryLoader.open();
  }
}
